import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  useReactTable,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  flexRender,
} from "@tanstack/react-table";
import { FaSearch, FaEdit, FaPlus, FaRegTrashAlt } from "react-icons/fa";
import SideNave from "../common/sidenav";
import TopNav from "../common/topnav";
import "../common/style.css";
import "./cv.css";
import { useNavigate, useLocation } from "react-router-dom";
import { deleteCVDataByID, getCVData, showImage } from "../http/api";
import Loading from "../loading/loading";
import { useAuth } from "../http/authcontex";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FaCopy } from "react-icons/fa6";

export default function ShowCVs() {
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const authContext = useAuth();
  const location = useLocation();

  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(
    localStorage.getItem("sidebarCollapsed") === "true"
  );

  const [cvDetails, setCvDetails] = useState([]);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });
  const [totalPages, setTotalPages] = useState(0);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [cvToDelete, setCvToDelete] = useState(null);

  // Filter state
  const [filters, setFilters] = useState({
    global: "",
    vertical: "",
    jobdesignation: "",
    name: "",
  });

  // Sidebar state persistence
  useEffect(() => {
    localStorage.setItem("sidebarCollapsed", isSidebarCollapsed);
  }, [isSidebarCollapsed]);

  const toggleSidebar = () => {
    setIsSidebarCollapsed(!isSidebarCollapsed);
    localStorage.setItem("sidebarCollapsed", !isSidebarCollapsed);
  };

  // Success toast on navigation
  useEffect(() => {
    if (location.state?.success) {
      toast.success("CV details submitted successfully!", {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        style: {
          border: "3px solid green",
          borderRadius: "8px",
          padding: "10px",
          bottom: "-45px",
        },
      });
    }
  }, [location.state]);

  // Fetch CV data with filters
  const fetchCVData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getCVData(
        pagination.pageIndex,
        pagination.pageSize,
        filters.global,
        filters.vertical,
        filters.name,
        filters.jobdesignation
      );

      if (response.status === 200) {
        const { content, totalPages: total } = response.data;
        setCvDetails(content);
        setTotalPages(total);
      }
    } catch (error) {
      if (error.response?.status === 401) {
        authContext.logout();
        navigate("");
      } else {
        console.error("Error fetching CV details:", error);
      }
    } finally {
      setLoading(false);
    }
  }, [
    authContext,
    navigate,
    pagination.pageIndex,
    pagination.pageSize,
    filters,
  ]);

 // Ref to track current pageIndex without causing effect to re-run
const pageIndexRef = React.useRef(pagination.pageIndex);

useEffect(() => {
  pageIndexRef.current = pagination.pageIndex; // keep ref updated
}, [pagination.pageIndex]);

useEffect(() => {
  if (pageIndexRef.current !== 0) {
    setPagination(prev => ({ ...prev, pageIndex: 0 }));
  }
}, [filters.global, filters.vertical, filters.name, filters.jobdesignation]);


  // Fetch data when pagination changes
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchCVData();
    }, 500);
    return () => clearTimeout(timer);
  }, [fetchCVData, pagination.pageIndex, pagination.pageSize]);


  // Debounced data fetching
  // useEffect(() => {
  //   const timer = setTimeout(() => {
  //     setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  //     fetchCVData();
  //   }, 500);

  //   return () => clearTimeout(timer);
  // }, [fetchCVData, filters]);

  // Action handlers
  const handleAdd = () => navigate("/add-cv");
  const handleEdit = useCallback(
    (cvDetail) => {
      navigate("/add-cv", { state: { cv: cvDetail, typ: "EDIT" } });
    },
    [navigate]
  );
  const handleCopy = useCallback(
    (cvDetail) => {
      navigate("/add-cv", { state: { cv: cvDetail, typ: "COPY" } });
    },
    [navigate]
  );
  const handleDelete = useCallback((id) => {
    setCvToDelete(id);
    setShowDeleteModal(true);
  }, []);

  const handleDeleteConfirm = async () => {
    if (!cvToDelete) return;
    try {
      const response = await deleteCVDataByID(cvToDelete);
      if (response.status === 200 || response.status === 204) {
        setCvDetails((prev) => prev.filter((cv) => cv.id !== cvToDelete));
        toast.success("CV data deleted successfully!");
      } else {
        toast.error("Failed to delete CV data.");
      }
    } catch (error) {
      console.error("Error deleting CV:", error);
      toast.error("Failed to delete CV data.");
    } finally {
      setShowDeleteModal(false);
      setCvToDelete(null);
    }
  };

  const handleDownload = async (filename) => {
    try {
      setLoading(true);
      if (!filename) return;

      const response = await showImage(filename);
      if (response.status === 200 || response.status === 202) {
        const blob = new Blob([response.data], {
          type: response.headers["content-type"],
        });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        a.click();
        URL.revokeObjectURL(url);
      } else {
        toast.error("Failed to download file");
      }
    } catch {
      toast.error("Error downloading file");
    } finally {
      setLoading(false);
    }
  };

  // Handle filter changes
  const handleFilterChange = (filterType, value) => {
    setFilters((prev) => ({ ...prev, [filterType]: value }));
  };

  // Table columns
  const columns = useMemo(
    () => [
      {
        accessorKey: "vertical",
        header: "Vertical",
        cell: (info) => info.getValue(),
      },
      {
        accessorKey: "jobdesignation",
        header: "Job Designation",
        cell: (info) => info.getValue(),
      },
      {
        accessorKey: "name",
        header: "Name",
        cell: (info) => info.getValue(),
        meta: { className: "cv-name" },
      },
      {
        accessorKey: "fileName",
        header: "CV File",
        cell: ({ row }) =>
          row.original.fileName ? (
            <button
              onClick={() => handleDownload(row.original.fileName)}
              className="btn btn-sm btn-success"
              title="Download CV"
            >
              Download
            </button>
          ) : (
            <span>No File</span>
          ),
        meta: { className: "cv-file" },
        enableSorting: false,
      },
      {
        accessorKey: "lastModifiedDate",
        header: "Last Update",
        cell: (info) => {
          const date = info.getValue();
          if (!date) return "N/A";
          try {
            return new Date(date).toLocaleString();
          } catch (e) {
            console.error("Error parsing date:", date, e);
            return "N/A";
          }
        },
        meta: { className: "last-modified" },
      },
      {
        id: "actions",
        header: "Actions",
        cell: ({ row }) => (
          <div className="d-flex gap-1">
            <button
              onClick={() => handleEdit(row.original)}
              className="btn btn-sm btn-primary"
            >
              <FaEdit />
            </button>
            <button
              onClick={() => handleCopy(row.original)}
              className="btn btn-sm bg-secondary"
            >
              <FaCopy />
            </button>
            <button
              onClick={() => handleDelete(row.original.id)}
              className="btn btn-sm btn-danger"
            >
              <FaRegTrashAlt />
            </button>
          </div>
        ),
        enableSorting: false,
      },
    ],
    [handleCopy, handleDelete, handleEdit]
  );

  // React Table instance
  const table = useReactTable({
    data: cvDetails,
    columns,
    pageCount: totalPages,
    state: { pagination },
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    manualPagination: true,
    manualFiltering: true,
  });

  return (
    <div className="ShowCVs">
      {loading && <Loading />}
      <div className={`sb-nav-fixed ${isSidebarCollapsed ? "collapsed" : ""}`}>
        <TopNav toggleSidebar={toggleSidebar} />
        <div id="layoutSidenav">
          <SideNave
            isSidebarCollapsed={isSidebarCollapsed}
            toggleSidebar={toggleSidebar}
          />
          <div id="layoutSidenav_content">
            <main>
              <div className="container-fluid px-4">
                <h4 className="mt-4">CVs</h4>
                <hr className="bg-warning" style={{ height: "4px" }} />

                {/* Search and Filter Controls */}
                <div className="mb-3">
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      <button className="btn-submit me-2" onClick={handleAdd}>
                        <FaPlus /> Add CV
                      </button>
                    </div>
                    <div className="d-flex align-items-center">
                      <input
                        type="text"
                        placeholder="Search all columns..."
                        className="form-control me-2"
                        value={filters.global}
                        onChange={(e) =>
                          handleFilterChange("global", e.target.value)
                        }
                      />
                      <button className="btn btn-sm btn-secondary">
                        <FaSearch />
                      </button>
                    </div>
                  </div>
                </div>

                {/* Main Table */}
                <div className="card mb-4">
                  <div className="card-body shadow-sm border-0">
                    <div className="table-responsive">
                      <table className="table table-striped table-hover">
                        <thead>
                          {table.getHeaderGroups().map((headerGroup) => (
                            <React.Fragment key={headerGroup.id}>
                              {/* Column headers */}
                              <tr>
                                {headerGroup.headers.map((header) => (
                                  <th key={header.id}>
                                    {flexRender(
                                      header.column.columnDef.header,
                                      header.getContext()
                                    )}
                                  </th>
                                ))}
                              </tr>
                              {/* Column filters */}
                              <tr>
                                {headerGroup.headers.map((header) => {
                                  const columnId = header.column.id;
                                  return (
                                    <th key={header.id}>
                                      {[
                                        "vertical",
                                        "jobdesignation",
                                        "name",
                                      ].includes(columnId) ? (
                                        <input
                                          type="text"
                                          value={filters[columnId] || ""}
                                          onChange={(e) =>
                                            handleFilterChange(
                                              columnId,
                                              e.target.value
                                            )
                                          }
                                          placeholder={`Filter ${columnId}...`}
                                          className="form-control form-control-sm"
                                        />
                                      ) : null}
                                    </th>
                                  );
                                })}
                              </tr>
                            </React.Fragment>
                          ))}
                        </thead>
                        <tbody>
                          {table.getRowModel().rows.length ? (
                            table.getRowModel().rows.map((row) => (
                              <tr key={row.id}>
                                {row.getVisibleCells().map((cell) => (
                                  <td
                                    key={cell.id}
                                    className={
                                      cell.column.columnDef.meta?.className ||
                                      ""
                                    }
                                  >
                                    {flexRender(
                                      cell.column.columnDef.cell,
                                      cell.getContext()
                                    )}
                                  </td>
                                ))}
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td
                                colSpan={columns.length}
                                className="text-center"
                              >
                                No CV data available.
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>

                    {/* Pagination Controls */}
                    <div className="d-flex justify-content-between align-items-center mt-3">
                      <div>
                        <button
                          className="btn btn-sm btn-outline-secondary me-2"
                          onClick={() => table.setPageIndex(0)}
                          disabled={!table.getCanPreviousPage()}
                        >
                          {"<<"}
                        </button>
                        <button
                          className="btn btn-sm btn-outline-secondary me-2"
                          onClick={() => table.previousPage()}
                          disabled={!table.getCanPreviousPage()}
                        >
                          {"<"}
                        </button>
                        <button
                          className="btn btn-sm btn-outline-secondary me-2"
                          onClick={() => table.nextPage()}
                          disabled={!table.getCanNextPage()}
                        >
                          {">"}
                        </button>
                        <button
                          className="btn btn-sm btn-outline-secondary"
                          onClick={() =>
                            table.setPageIndex(table.getPageCount() - 1)
                          }
                          disabled={!table.getCanNextPage()}
                        >
                          {">>"}
                        </button>
                      </div>
                      <span className="d-flex align-items-center">
                        Page{" "}
                        <strong>
                          {table.getState().pagination.pageIndex + 1} of{" "}
                          {table.getPageCount()}
                        </strong>
                        {" | Go to page: "}
                        <input
                          type="number"
                          defaultValue={
                            table.getState().pagination.pageIndex + 1
                          }
                          onChange={(e) => {
                            const page = e.target.value
                              ? Number(e.target.value) - 1
                              : 0;
                            table.setPageIndex(page);
                          }}
                          className="form-control form-control-sm mx-2"
                          style={{ width: "70px" }}
                        />
                      </span>
                      <select
                        value={table.getState().pagination.pageSize}
                        onChange={(e) => {
                          table.setPageSize(Number(e.target.value));
                        }}
                        className="form-select form-select-sm"
                        style={{ width: "120px" }}
                      >
                        {[10, 20, 30, 40, 50].map((size) => (
                          <option key={size} value={size}>
                            Show {size}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
              </div>
            </main>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div
          className="modal fade show"
          style={{ display: "block" }}
          onClick={() => setShowDeleteModal(false)}
        >
          <div
            className="modal-dialog modal-dialog-centered"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Confirm Deletion</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowDeleteModal(false)}
                  aria-label="Close"
                ></button>
              </div>
              <div className="modal-body">
                Are you sure you want to delete this CV data?
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowDeleteModal(false)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={handleDeleteConfirm}
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Toast Container */}
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
    </div>
  );
}
